import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  getLiveTeam,
  LiveTeamPerson,
  LiveTeamState,
  LiveTeamTab,
} from "../../data/api/liveTeamApi";

/**
 * A live client's team, as the TENANT has it.
 *
 * Master stopped owning this at go-live: its three team steps are one-shot, and
 * the place seats actually change is the tenant's own board. So this tab is
 * read-only by design — the button hands the CA over to that board rather than
 * offering a second, diverging editor.
 *
 * Opening the tab is also a repair. The server refreshes master's
 * `onboarding_module_seats` from what the tenant just said, and fills in any
 * blank `user_company_assignments.role_name` — the field "Enter Dashboard"
 * refuses to proceed without, and which the tenant only pushes when a role
 * CHANGES, so people seated after go-live never got one (ZOUR-54).
 */

function PeopleLine({ label, people }: { label: string; people: LiveTeamPerson[] }) {
  return (
    <div className="flex w-full items-start gap-4">
      <span className="w-24 flex-none text-body-bold font-body-bold text-default-font">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {people.length === 0 ? (
          <span className="text-body font-body text-subtext-color">—</span>
        ) : (
          people.map((person) => (
            <Badge key={person.user_id} variant="neutral">
              {person.name}
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}

function GateBadge({ tab }: { tab: LiveTeamTab }) {
  switch (tab.gate?.status) {
    case "block":
      return <Badge variant="error">Needs attention</Badge>;
    case "warn":
      return <Badge variant="warning">Check this</Badge>;
    case "empty":
      return <Badge variant="neutral">Nobody seated</Badge>;
    default:
      return <Badge variant="success">OK</Badge>;
  }
}

function TabCard({ tab }: { tab: LiveTeamTab }) {
  const reviews = tab.functions.filter((fn) => fn.policy === "review");
  const blocked = tab.functions.filter((fn) => fn.policy === "blocked");

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-white px-6 py-5">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">{tab.label}</span>
        <GateBadge tab={tab} />
      </div>

      <div className="flex w-full flex-col items-start gap-3">
        <PeopleLine label="Makers" people={tab.makers} />
        <PeopleLine label="Checkers" people={tab.checkers} />
        <div className="flex w-full items-start gap-4">
          <span className="w-24 flex-none text-body-bold font-body-bold text-default-font">
            Reviewed
          </span>
          <span className="text-body font-body text-neutral-600">
            {reviews.length === 0
              ? "Nothing on this tab waits for a checker."
              : `${reviews.length} function${reviews.length === 1 ? "" : "s"} go through a checker: ${reviews
                  .map((fn) => fn.label)
                  .join(", ")}.`}
            {blocked.length > 0 &&
              ` ${blocked.length} checker-only: ${blocked.map((fn) => fn.label).join(", ")}.`}
          </span>
        </div>
      </div>

      {tab.gate?.message && (
        <span className="text-caption font-caption text-subtext-color">{tab.gate.message}</span>
      )}
    </div>
  );
}

export const CompanyTeamTab: React.FC<{ companyId: number; companyAppUrl: string }> = ({
  companyId,
  companyAppUrl,
}) => {
  const [state, setState] = useState<LiveTeamState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entering, setEntering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setState(await getLiveTeam(companyId));
    } catch (err: any) {
      setError(err?.message || "Could not read the team.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardUrl = state?.team_board_url || `${companyAppUrl}/settings/team-assignment`;

  /**
   * Hand the CA over to the tenant's board. The normal Enter flow, so they
   * arrive with a real tenant session; `next` says where to land. A master
   * admin who is not assigned to this company cannot use that flow at all —
   * then the plain URL is still better than a dead button.
   */
  const manageOnTenant = async () => {
    setEntering(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/company-selection/enter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ company_id: companyId }),
      });

      if (!response.ok) {
        window.open(boardUrl, "_blank", "noopener");
        return;
      }

      const data = await response.json();
      const params = new URLSearchParams({
        token: data.data.token,
        company_id: companyId.toString(),
        master_admin_url: window.location.origin,
        next: "/settings/team-assignment",
      });
      window.location.href = `${data.data.company.app_url}/from-master?${params.toString()}`;
    } catch {
      window.open(boardUrl, "_blank", "noopener");
    } finally {
      setEntering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start px-8 py-6">
        <span className="text-body font-body text-subtext-color">
          Reading the team from the tenant…
        </span>
      </div>
    );
  }

  const board = state?.board ?? null;
  const refresh = state?.refresh ?? null;

  return (
    <div className="flex w-full flex-col items-start gap-6 px-8 py-6">
      <div className="flex w-full items-start justify-between gap-4">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">Team</span>
          <span className="text-body font-body text-subtext-color">
            Who makes and who checks, per module. The tenant owns this once the client is live — this
            page reads it back.
          </span>
        </div>
        <div className="flex flex-none items-center gap-2">
          <Button variant="neutral-secondary" onClick={() => void load()}>
            Refresh
          </Button>
          <Button disabled={entering} onClick={() => void manageOnTenant()}>
            Manage on tenant
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-error-100 bg-error-50 px-4 py-3">
          <span className="text-body font-body text-error-800">{error}</span>
        </div>
      )}

      {!error && state?.unreachable && (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
          <span className="text-body-bold font-body-bold text-default-font">
            Couldn't reach the tenant, so there is no team to show.
          </span>
          <span className="text-body font-body text-subtext-color">{state.unreachable}</span>
          <a
            className="text-body font-body text-brand-700 underline"
            href={boardUrl}
            target="_blank"
            rel="noreferrer"
          >
            {boardUrl}
          </a>
        </div>
      )}

      {board && (
        <>
          {refresh && (
            <span className="text-caption font-caption text-subtext-color">
              Master's copy brought into line: {refresh.seats_written} seat
              {refresh.seats_written === 1 ? "" : "s"}
              {refresh.role_names_filled > 0 &&
                `, ${refresh.role_names_filled} role name${refresh.role_names_filled === 1 ? "" : "s"} filled in`}
              {refresh.assignments_created > 0 &&
                `, ${refresh.assignments_created} assignment${refresh.assignments_created === 1 ? "" : "s"} created`}
              .
            </span>
          )}

          {board.validation.blocks.map((block, index) => (
            <div
              key={`block-${index}`}
              className="flex w-full items-start rounded-md border border-solid border-error-100 bg-error-50 px-4 py-3"
            >
              <span className="text-body font-body text-error-800">{block.message}</span>
            </div>
          ))}

          <div className="flex w-full flex-col items-start gap-4">
            {board.tabs.map((tab) => (
              <TabCard key={tab.key} tab={tab} />
            ))}
          </div>

          {board.unseated.length > 0 && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-6 py-5">
              <span className="text-body-bold font-body-bold text-default-font">
                On the tenant but on no tab
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {board.unseated.map((person) => (
                  <Badge key={person.user_id} variant="neutral">
                    {person.name}
                  </Badge>
                ))}
              </div>
              <span className="text-caption font-caption text-subtext-color">
                They can sign in but hold no module permissions until someone seats them.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
